import { ReactElement, useState, KeyboardEvent } from "react";
import { useForm, Controller } from "react-hook-formV7";
import { ClearEditorPlugin } from "@twilio-paste/core/lexical-library";
import {
  ChatComposerPlugins,
  CompositeChatComposer,
} from "@twiliointernal/org/twilio/e2-commons/shared/ui";
import {
  SendMessageFormValues,
  dataTestIds,
} from "@twiliointernal/org/twilio/e2-commons/shared/definitions";
import { LiveChatComposerProps, copy } from "./definitions";
import chatService from "../../services/conversations/conversationsService";

const logError = (error: Error): void => {
  console.error(error);
};

const ChatComposer = ({
  onError = logError,
}: LiveChatComposerProps): ReactElement => {
  const { handleSubmit, control, formState } = useForm<SendMessageFormValues>();
  const { isValid, isSubmitting } = formState;

  // Allow toggling of required state when using attachments
  const [isRequired] = useState(false);

  const [disabled, setDisabled] = useState(false);

  const formHandler = (data: SendMessageFormValues): void => {
    if (data.input && data.input.trim().length > 0) {
      setDisabled(true);
      chatService.sendMessage(data.input).finally(() => {
        setDisabled(false);
      });
    }
  };

  const reportTyping = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter") {
      chatService.startTyping();
    }
  };

  return (
    <Controller
      name={"input"}
      control={control}
      rules={{ required: isRequired, minLength: 1 }}
      render={({ field }) => (
        <CompositeChatComposer
          config={{
            namespace: "live-chat-message",
            onError,
          }}
          disabled={disabled}
          ariaLabel={copy.input.ariaLabel}
          placeholder={copy.input.placeholder}
          required
          {...field}
          onKeyDown={reportTyping}
          data-testid={dataTestIds.liveChat.chatComposer}
        >
          <ClearEditorPlugin />
          <ChatComposerPlugins.ChatSendButtonPlugin
            loading={isSubmitting}
            isChatActive={true}
            shouldClearInput={isValid && isSubmitting}
            disabled={disabled}
            handleOnEnter={handleSubmit(formHandler)}
            handleOnClick={handleSubmit(formHandler)}
          />
        </CompositeChatComposer>
      )}
    />
  );
};

export default ChatComposer;
